/**
 * Amos-64 — Dorella-memory review layer for the Convex Loss Run queue.
 *
 * Reviewed/New/Changed badges, the what-changed panel, and the feedback
 * disposition chip. Everything here is a read-only overlay over audit
 * metadata; the ONLY write paths are PATCH action=mark_reviewed (marker)
 * and POST /convex/feedback action=resolve — neither is read by any
 * approve/send/guard logic.
 */
import { useState } from 'react';
import { api } from '../api/client';

// ─────────────────────────────────────────────────────────────────────────────
// CONFIG — single point of adjustment (CP1 ruling: Dorella's semantics answer
// may change these before deploy).
//
// MATERIAL_EVENT_FIELDS: timestamps that flip REVIEWED → CHANGED when they
// postdate last_reviewed_at, and that populate the what-changed timeline.
// AUTO_STAMP_ACTIONS: which successful UI actions auto-mark a record reviewed
// (an explicit "Mark reviewed" button always exists regardless).
// Opening a record NEVER stamps (CP0 ruling 1).
// ─────────────────────────────────────────────────────────────────────────────

export const MATERIAL_EVENT_FIELDS: { field: string; label: string }[] = [
  { field: 'parsed_at', label: 'Re-parsed' },
  { field: 'rendered_at', label: 'Document rendered' },
  { field: 'recipients_corrected_at', label: 'Recipients corrected' },
  { field: 'recipient_verified_at', label: 'Recipients verified' },
  { field: 'skippy_verified_at', label: 'Nil-verified' },
  { field: 'auto_verified_at', label: 'Auto-verified' },
  { field: 'prior_delivery_detected_at', label: 'Prior delivery flagged' },
  { field: 'nil_guard_demoted_at', label: 'Nil-guard demoted' },
  { field: 'recipient_guard_at', label: 'Recipient-guard flagged' },
  { field: 'freshness_hold_at', label: 'Freshness hold' },
  { field: 'duplicate_hold_at', label: 'Duplicate hold' },
  // Amos-103 item 6: a note written for the operator resurfaces the card (New + Changed).
  { field: 'note_added_at', label: 'Note added' },
  { field: 'approved_at', label: 'Approved' },
  { field: 'archived_at', label: 'Archived' },
  { field: 'deleted_at', label: 'Deleted' },
];

export const AUTO_STAMP_ACTIONS: Record<string, boolean> = {
  approve: true,
  send_anyway: true,
  correct_recipients: true,
  verify_recipients: true,
  archive: true,
  archive_duplicate: true,
  feedback: true,
  edit_notes: true, // Amos-103: the operator's own note save must not flip her card to Changed
  soft_delete: false, // record leaves the queue; marker would be moot
};

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface ReviewHistoryEntry {
  at: string;
  by: string;
  source: string;
}

export interface FeedbackEntry {
  feedback_id: string;
  module: string;
  timestamp: string;
  user_email?: string;
  feedback?: string;
  reference_number?: string;
  page_url?: string;
  request_id?: string;
  received_at?: string;
  received_by?: string;
  resolved_at?: string;
  resolved_by?: string;
  resolved_note?: string;
}

export type ReviewState = 'new' | 'reviewed' | 'changed';

export interface ReviewVerdict {
  state: ReviewState;
  /** Most recent material event that postdates the marker (CHANGED only). */
  trigger?: { label: string; at: string };
}

interface ReviewableRecord {
  request_id: string;
  last_reviewed_at?: string;
  last_reviewed_by?: string;
  review_history?: ReviewHistoryEntry[];
  send_attempts?: { attempted_at?: string }[];
  [key: string]: any;
}

// ─────────────────────────────────────────────────────────────────────────────
// Logic
// ─────────────────────────────────────────────────────────────────────────────

/** All dated material events on a record, newest first. */
export function materialEvents(req: ReviewableRecord): { label: string; at: string }[] {
  const events: { label: string; at: string }[] = [];
  for (const { field, label } of MATERIAL_EVENT_FIELDS) {
    const at = req[field];
    if (typeof at === 'string' && at) events.push({ label, at });
  }
  for (const a of req.send_attempts || []) {
    if (a.attempted_at) events.push({ label: 'Send attempt', at: a.attempted_at });
  }
  return events.sort((x, y) => (x.at < y.at ? 1 : -1));
}

export function reviewVerdict(req: ReviewableRecord): ReviewVerdict {
  const marker = req.last_reviewed_at;
  if (!marker) return { state: 'new' };
  const newer = materialEvents(req).filter(e => e.at > marker);
  if (newer.length > 0) return { state: 'changed', trigger: newer[0] };
  return { state: 'reviewed' };
}

/**
 * Fire the review auto-stamp after a substantive action succeeded.
 * Never throws and never blocks the action it follows — a failed stamp
 * degrades to the record simply staying "New"/"Changed".
 */
export async function autoStamp(requestId: string, action: string): Promise<void> {
  if (!AUTO_STAMP_ACTIONS[action]) return;
  try {
    await api.markConvexReviewed(requestId, `auto:${action}`);
  } catch (err) {
    console.warn(`review auto-stamp (${action}) failed for ${requestId}:`, err);
  }
}

/**
 * Derive the record binding of a feedback entry: the stored request_id
 * (post-Amos-64 submissions) or the request UUID embedded in page_url.
 * NO reference-number guessing (CP1 ruling 4) — unbindable entries go to
 * the unmatched bucket.
 */
const PAGE_URL_REQUEST_RE = /\/convex\/request\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})/i;

export function feedbackRequestId(entry: FeedbackEntry): string | null {
  if (entry.request_id) return entry.request_id;
  const m = (entry.page_url || '').match(PAGE_URL_REQUEST_RE);
  return m ? m[1].toLowerCase() : null;
}

export function bindFeedback(entries: FeedbackEntry[]): {
  byRequest: Record<string, FeedbackEntry[]>;
  unmatched: FeedbackEntry[];
} {
  const byRequest: Record<string, FeedbackEntry[]> = {};
  const unmatched: FeedbackEntry[] = [];
  for (const e of entries) {
    const id = feedbackRequestId(e);
    if (id) (byRequest[id] = byRequest[id] || []).push(e);
    else unmatched.push(e);
  }
  return { byRequest, unmatched };
}

export function feedbackDisposition(e: FeedbackEntry): 'resolved' | 'acknowledged' | 'open' {
  if (e.resolved_at) return 'resolved';
  if (e.received_by) return 'acknowledged';
  return 'open';
}

// ─────────────────────────────────────────────────────────────────────────────
// Components
// ─────────────────────────────────────────────────────────────────────────────

const REVIEW_BADGE_CONFIG: Record<ReviewState, { label: string; className: string }> = {
  new:      { label: 'NEW',        className: 'bg-blue-100 text-blue-700 border border-blue-200' },
  reviewed: { label: 'Reviewed ✓', className: 'bg-emerald-100 text-emerald-700 border border-emerald-200' },
  changed:  { label: 'CHANGED',    className: 'bg-orange-100 text-orange-700 border border-orange-300' },
};

export function ReviewBadge({ req }: { req: ReviewableRecord }) {
  const v = reviewVerdict(req);
  const cfg = REVIEW_BADGE_CONFIG[v.state];
  const title =
    v.state === 'new' ? 'Never marked reviewed' :
    v.state === 'reviewed' ? `Reviewed ${req.last_reviewed_at} by ${req.last_reviewed_by || '?'}` :
    `${v.trigger!.label} at ${v.trigger!.at} — after last review (${req.last_reviewed_at})`;
  return (
    <span title={title} className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export function FeedbackChip({ entries }: { entries?: FeedbackEntry[] }) {
  if (!entries || entries.length === 0) return null;
  const open = entries.filter(e => feedbackDisposition(e) === 'open').length;
  return (
    <span
      title={`${entries.length} feedback entr${entries.length === 1 ? 'y' : 'ies'} on this record — ${open} open`}
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
        open > 0 ? 'bg-purple-100 text-purple-700 border border-purple-300' : 'bg-slate-100 text-slate-500 border border-slate-200'
      }`}
    >
      💬 {entries.length}{open > 0 ? ` · ${open} open` : ''}
    </span>
  );
}

const DISPOSITION_CHIP: Record<string, { label: string; className: string }> = {
  open:         { label: 'Open',     className: 'bg-purple-100 text-purple-700' },
  acknowledged: { label: "Ack'd",    className: 'bg-blue-100 text-blue-700' },
  resolved:     { label: 'Resolved', className: 'bg-emerald-100 text-emerald-700' },
};

/** What-changed panel + Mark reviewed button, for the detail page. */
export function WhatChangedPanel({ req, onStamped }: { req: ReviewableRecord; onStamped: () => void }) {
  const [stamping, setStamping] = useState(false);
  const [stampError, setStampError] = useState<string | null>(null);
  const v = reviewVerdict(req);
  const marker = req.last_reviewed_at;
  const events = materialEvents(req);
  const since = marker ? events.filter(e => e.at > marker) : events;
  const earlier = marker ? events.filter(e => e.at <= marker) : [];

  const handleMarkReviewed = async () => {
    setStamping(true);
    setStampError(null);
    try {
      await api.markConvexReviewed(req.request_id, 'manual');
      onStamped();
    } catch (err: any) {
      setStampError(err.message || 'Mark reviewed failed');
    } finally {
      setStamping(false);
    }
  };

  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-slate-200 px-4 py-3">
      <div className="flex items-center gap-3 mb-2">
        <h3 className="text-sm font-semibold text-slate-700">
          {marker ? 'What changed since last review' : 'Record history'}
        </h3>
        <ReviewBadge req={req} />
        {marker && (
          <span className="text-xs text-slate-400">
            last reviewed {marker.slice(0, 16).replace('T', ' ')} by {req.last_reviewed_by || '?'}
          </span>
        )}
        <button
          onClick={handleMarkReviewed}
          disabled={stamping || v.state === 'reviewed'}
          title={v.state === 'reviewed' ? 'Nothing changed since the last review' : 'Stamp this record as reviewed'}
          className="ml-auto px-3 py-1 text-xs font-medium rounded-md border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          {stamping ? 'Marking…' : 'Mark reviewed'}
        </button>
      </div>
      {stampError && <p className="text-xs text-red-600 mb-2">{stampError}</p>}
      {since.length === 0 ? (
        <p className="text-xs text-slate-400">No material events{marker ? ' since your last review' : ' recorded'}.</p>
      ) : (
        <ul className="space-y-1">
          {since.map((e, i) => (
            <li key={i} className="text-xs text-slate-600 flex gap-2">
              <span className="text-slate-400 font-mono whitespace-nowrap">{e.at.slice(0, 16).replace('T', ' ')}</span>
              <span className={marker ? 'font-medium text-orange-700' : ''}>{e.label}</span>
            </li>
          ))}
        </ul>
      )}
      {earlier.length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
            Earlier history ({earlier.length})
          </summary>
          <ul className="space-y-1 mt-1">
            {earlier.map((e, i) => (
              <li key={i} className="text-xs text-slate-500 flex gap-2">
                <span className="text-slate-400 font-mono whitespace-nowrap">{e.at.slice(0, 16).replace('T', ' ')}</span>
                <span>{e.label}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
      {(req.review_history || []).length > 0 && (
        <details className="mt-2">
          <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">
            Review history ({(req.review_history || []).length})
          </summary>
          <ul className="space-y-1 mt-1">
            {[...(req.review_history || [])].reverse().map((h, i) => (
              <li key={i} className="text-xs text-slate-500 flex gap-2">
                <span className="text-slate-400 font-mono whitespace-nowrap">{h.at.slice(0, 16).replace('T', ' ')}</span>
                <span>{h.by}</span>
                <span className="text-slate-400">({h.source})</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}

/** Bound feedback entries for one record, with resolve action (detail page). */
export function FeedbackSection({ entries, onResolved }: { entries: FeedbackEntry[]; onResolved: () => void }) {
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  if (entries.length === 0) return null;

  const handleResolve = async (feedbackId: string) => {
    const note = window.prompt('Resolution note (optional):') ?? '';
    setResolvingId(feedbackId);
    setResolveError(null);
    try {
      await api.resolveConvexFeedback(feedbackId, note);
      onResolved();
    } catch (err: any) {
      setResolveError(err.message || 'Resolve failed');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="mb-4 bg-white rounded-lg shadow-sm border border-purple-200 px-4 py-3">
      <h3 className="text-sm font-semibold text-slate-700 mb-2">
        💬 Feedback on this record ({entries.length})
      </h3>
      {resolveError && <p className="text-xs text-red-600 mb-2">{resolveError}</p>}
      <ul className="space-y-2">
        {entries.map(e => {
          const disp = feedbackDisposition(e);
          const chip = DISPOSITION_CHIP[disp];
          return (
            <li key={e.feedback_id} className="text-xs text-slate-600 flex items-start gap-2">
              <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${chip.className}`}>
                {chip.label}
              </span>
              <span className="text-slate-400 font-mono whitespace-nowrap">{(e.timestamp || '').slice(0, 10)}</span>
              <span className="flex-1">
                {e.feedback}
                <span className="text-slate-400"> — {e.user_email}</span>
                {e.resolved_note && <span className="block text-emerald-600 mt-0.5">↳ {e.resolved_note}</span>}
              </span>
              {disp !== 'resolved' && (
                <button
                  onClick={() => handleResolve(e.feedback_id)}
                  disabled={resolvingId === e.feedback_id}
                  className="px-2 py-0.5 text-[11px] font-medium rounded border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 whitespace-nowrap"
                >
                  {resolvingId === e.feedback_id ? '…' : 'Resolve'}
                </button>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** Collapsible bucket for feedback with no derivable record link (queue page). */
export function UnmatchedFeedbackBucket({ entries }: { entries: FeedbackEntry[] }) {
  if (entries.length === 0) return null;
  return (
    <details className="mb-4 bg-white rounded-lg shadow-sm border border-slate-200 px-4 py-3">
      <summary className="text-sm font-medium text-slate-600 cursor-pointer">
        💬 Feedback without a record link ({entries.length})
      </summary>
      <p className="text-xs text-slate-400 mt-1 mb-2">
        These entries were submitted without a record page context and can't be matched with certainty — no guessing.
      </p>
      <ul className="space-y-2">
        {entries.map(e => {
          const disp = feedbackDisposition(e);
          const chip = DISPOSITION_CHIP[disp];
          return (
            <li key={e.feedback_id} className="text-xs text-slate-600 flex items-start gap-2">
              <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${chip.className}`}>
                {chip.label}
              </span>
              <span className="text-slate-400 font-mono whitespace-nowrap">{(e.timestamp || '').slice(0, 10)}</span>
              <span className="flex-1">
                {e.feedback}
                <span className="text-slate-400"> — {e.user_email}{e.reference_number ? ` · ref "${e.reference_number}"` : ''}</span>
              </span>
            </li>
          );
        })}
      </ul>
    </details>
  );
}
